import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';

/**
 * Handle loading google maps if window.google is undefined. Returns the Google
 * maps instance, a ref, and an initialized flag for use in other components.
 * @param {String} apiKey - Google maps API key
 * @param {Object} options - Google maps options
 * @param {Object} style - Google maps styles
 */
const useGoogleMap = ({ apiKey, options, styles }) => {
  const [mapState, setMapState] = useState({
    mapInstance: null,
    initialized: false,
  });

  const mapRef = useRef();

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  useEffect(() => {
    const handleScriptLoad = () => {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        ...options,
        ...styles,
        mapTypeControl: false,
        mapTypeId: basemap ? basemap : window.google.maps.MapTypeId.ROADMAP,
      });

      setMapState({
        ...mapState,
        mapInstance,
        initialized: true,
      });

      exportToGlobalNamespace('map', {
        mapProvider: 'google',
        mapInstance,
      });
    };

    const loadMapScript = () => {
      if (window.google) {
        handleScriptLoad();
        return;
      }
      if (window.alreadyRequestedGoogleMaps) {
        const gmapsScript = document.getElementById('google-maps-api');
        if (gmapsScript) {
          gmapsScript.addEventListener('load', handleScriptLoad);
        }
        return;
      }
      window.alreadyRequestedGoogleMaps = true;
      const s = document.createElement('script');
      s.id = 'google-maps-api';
      s.type = 'text/javascript';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=quarterly&callback=Function.prototype`;
      const x = document.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
      s.addEventListener('load', handleScriptLoad);
    };

    loadMapScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanUpMap = () => {
    if (mapState.mapInstance) {
      mapState.mapInstance.setMap(null);
      // TODO: confirm if this works
      window.google.maps.event.clearInstanceListeners(window);
      window.google.maps.event.clearInstanceListeners(document);
    }
  };

  return { mapRef, cleanUpMap, ...mapState };
};

export default useGoogleMap;
